import { Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { FormValues, SavedViewForm } from "components/views/SavedViewForm";
import { useSavedViewAlert } from "lib/hooks/api/lead_subscriptions/useSavedViewAlert";
import { useDuplicateSavedView } from "lib/hooks/api/notices/views/useDuplicateSavedView";
import { LeadSubscriptionFrequency } from "lib/types/graphQLEnums";
import { ModalView } from "./utils";

type DuplicateViewModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean, newViewId?: string) => void;
  selectedView: ModalView;
};

export function DuplicateViewModal({
  isOpen,
  selectedView,
  onOpenChange,
}: DuplicateViewModalProps) {
  const { alert: personalSavedViewAlert, isLoading: isLoadingSubscriptionContents } =
    useSavedViewAlert(selectedView.id);

  const { mutate: duplicateSavedView, isLoading: isLoadingDuplicate } = useDuplicateSavedView({
    onSuccess: async (data, vars) => {
      onOpenChange(false, data.duplicateSavedView.id);
      toast.success(`${vars.request.name} duplicated successfully!`);
    },
    onError: async (_data) => {
      toast.error("Failed to duplicate search");
    },
  });

  const handleSubmit = async (data: FormValues): Promise<void> => {
    duplicateSavedView({
      request: {
        id: selectedView.id,
        name: data.viewName,
        description: data.viewDescription ?? "",
        alertFrequency:
          data.alertFrequency === "NEVER"
            ? undefined
            : (data.alertFrequency as LeadSubscriptionFrequency),
      },
    });
  };

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} padding={24}>
      {isLoadingSubscriptionContents ? (
        <Skeleton style={{ width: 570, height: 340 }} paragraph={{ rows: 8 }} />
      ) : (
        <>
          <Flex column gap={8}>
            <Text h3>Duplicate "{selectedView.name}"?</Text>
            <Text color="sysTextSecondary">
              Quickly access your most commonly used filters at anytime by creating filtered
              searches.
            </Text>
          </Flex>
          <SavedViewForm
            defaultValues={{
              viewName: `${selectedView.name} (Copy)`,
              viewDescription: selectedView.description ?? "",
              alertFrequency: personalSavedViewAlert?.id
                ? personalSavedViewAlert.frequency
                : "NEVER",
            }}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            isLoading={isLoadingDuplicate}
          />
        </>
      )}
    </Modal>
  );
}
